import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import SiteNav from "../components/SiteNav";
import SiteFooter from "../components/SiteFooter";
import BackBar from "../components/BackBar";
import StackTheDeck from "../components/StackTheDeck";
import CharterBonusBanner from "../components/CharterBonusBanner";
import Brand from "../components/Brand";
import ThreeTierPricing from "../components/ThreeTierPricing";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Check } from "lucide-react";

const TIERS = {
  faithful: {
    name: "Pro", tagline: "Everything Faitharama offers — one simple price",
    monthly: { id: "faithful_monthly", price: 49 },
    annual:  { id: "faithful_annual",  price: 490, effective: 40.83 },
    features: [
      { strong: "faitharama.com/u/your-name", sub: "Your public page on a real Christian domain" },
      { strong: "Your face in the Member Directory", sub: "Visitors search Faitharama and find YOU" },
      { strong: "10,000 contacts (UNLIMITED on $79)", sub: "Manage your full list" },
      { strong: "UNLIMITED funnels & landing pages", sub: "44+ Faith · Wellness · Business templates" },
      { strong: "ALL 10 courses FREE — usually $10 each", sub: "Faith, wellness, evangelism, $1K business" },
      { strong: "Free Guide + email gating", sub: "Build your list while you sleep" },
      { strong: "Email broadcasts to your whole list", sub: "Sermons, devotionals, prayer requests" },
      { strong: "52-week devotional automation", sub: "A full year of pre-written content" },
      { strong: "Flipbooks on every product you sell", sub: "Magazine-quality lead magnets and book covers" },
      { strong: "Christian merch + supplement store", sub: "Print-on-demand + senior-friendly supplements" },
      { strong: "50/50 affiliate split — flat, no tiers", sub: "Same on $49 and $79 — no hoops" },
      { strong: "Direct line to Mike — Help Me, Mike! button", sub: "Real human reply, usually within 24 hrs" },
      { strong: "7-day free trial · Cancel anytime", sub: "Stripe-secured payments · 30-day money-back" },
    ],
    popular: true,
  },
};

export default function Pricing({ embedded = false }) {
  const [annual, setAnnual] = useState(true);
  const { user } = useAuth();
  const nav = useNavigate();

  const start = async (tierKey) => {
    const plan = TIERS[tierKey][annual ? "annual" : "monthly"];
    if (!user) {
      toast.info("Create your free account first — we'll send you straight to checkout.");
      nav(`/signup?plan=${plan.id}`);
      return;
    }
    try {
      const r = await api.post("/subscription/checkout", { plan_id: plan.id, origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) { toast.error(e.response?.data?.detail || "Checkout failed"); }
  };

  return (
    <div className="min-h-screen bg-ivory">
      {!embedded && <SiteNav />}
      {!embedded && <BackBar />}

      {/* NEW 3-tier flagship pricing block — drives the launch */}
      <ThreeTierPricing />

      <section className="max-w-6xl mx-auto px-6 md:px-8 py-16 text-center">
        <div className="text-xs tracking-wider-cap text-gold-deep mb-3">Simple, honest pricing</div>
        <h1 className="font-serif-display text-5xl md:text-6xl text-red-wine">2 Simple Plans. $49 or $79. 7-Day Free Trial.</h1>
        <div className="divider-gold"></div>
        <p className="text-base text-foreground max-w-2xl mx-auto mb-8"><strong>Pro ($49/mo)</strong> and <strong>Unlimited ($79/mo)</strong> both earn the same <strong>flat 50% of what Faitharama.com gets</strong> on every sale, forever. The only difference between the two: <strong>contacts and book covers.</strong> Everything else is the same.</p>

        {/* FAST START PROGRAM — Mike's $1,000/month in 90 days entry product for free members */}
        {/* FAST START GUIDE — Mike's included entry product (replaced the old Fast Start Program block per Mike's call: "tear down the Faitharama Bundle for included, it looks horrible and is priced wrong") */}
        <div className="mt-2 mb-12 max-w-5xl mx-auto" data-testid="plan-fast-start">
          <div className="rounded-3xl overflow-hidden shadow-2xl border-4 border-[#F39A1F] bg-gradient-to-br from-[#FFFFFF] to-[#EAF6FC]">
            <div className="p-6 md:p-8 text-center">
              <div className="text-[10px] tracking-[0.32em] uppercase font-bold text-gold-deep">★ ONE-TIME · NOT A SUBSCRIPTION ★</div>
              <h3 className="font-serif-display text-2xl md:text-3xl text-red-wine uppercase mt-1"><Brand /> Fast Start Guide</h3>
              <p className="text-foreground mt-2 leading-relaxed max-w-2xl mx-auto">
                <strong className="text-red-wine">Mike's plain-English playbook</strong> for hitting $1,000/month in 90 days — even during your 7-day free trial.
              </p>
              <p className="text-foreground mt-2 leading-relaxed max-w-2xl mx-auto">
                Digital PDF guide + 7 video walk-throughs. One-time payment.
              </p>
              <div className="mt-3 font-serif-display text-4xl text-red-wine">included</div>
              <Link to="/starter-guide" data-testid="cert-cta-pricing"
                    className="mt-4 inline-flex items-center gap-2 bg-red-wine text-[#FFFFFF] px-7 py-3.5 rounded-md text-sm font-bold uppercase tracking-[0.18em] hover:bg-[#1F8A2A] shadow-lg transition">
                Get The Fast Start Guide — included
              </Link>
            </div>
          </div>
        </div>

        {/* CHARTER BONUS BANNER — first 100 paid members get Mike's book free */}
        <div className="max-w-5xl mx-auto mb-8">
          {/* CharterBonusBanner removed at Mike's request — was creating false scarcity */}
        </div>

        {/* Old AllAccessBundle removed — ThreeTierPricing at the top of this page
            already shows all 3 plans ($0 / $49 / $79) properly. */}

        {/* STACK THE DECK — what you'd pay separately */}
        <div className="max-w-5xl mx-auto mb-12">
          <StackTheDeck />
        </div>

        {/* Old billing toggle (Monthly/Annual) — REMOVED. ThreeTierPricing
            already shows monthly + annual pricing inline. */}

        {/* Money-back / Guarantee strip */}
        <div className="mt-12 max-w-3xl mx-auto p-7 rounded-2xl border-2 text-left" style={{borderColor:'#F39A1F', background:'linear-gradient(135deg, #FFFFFF 0%, #FFFFFF 100%)'}}>
          <div className="text-center">
            <div className="text-[11px] tracking-wider-cap text-gold-deep font-bold">Mike's Personal Promise</div>
            <h3 className="font-serif-display text-3xl text-red-wine mt-2 uppercase">30-Day Money-Back Promise</h3>
          </div>
          <p className="text-base text-foreground mt-3 text-center">Try <Brand/> Pro ($49/mo) or Unlimited ($79/mo). If within 30 days you don&apos;t feel it&apos;s serving your ministry — email <span className="text-red-wine font-bold">team@<Brand suffix=".com"/></span> and I&apos;ll refund every penny. No hoops, no questions.</p>
          <p className="text-base text-foreground mt-3 text-center italic">"Either way — God bless you, friend." — Mike</p>
        </div>

        {/* FAQ */}
        <div className="mt-12 max-w-3xl mx-auto text-left">
          <h3 className="font-serif-display text-3xl text-red-wine uppercase text-center">Common Questions</h3>
          <div className="divider-gold"></div>
          <div className="space-y-4 mt-6">
            {[
              ["Do I need a credit card to start?", "The 7-day free trial needs no card up front. After 7 days, Pro is $49/mo or Unlimited is $79/mo, both with a 30-day money-back guarantee, cancel anytime."],
              ["Can I cancel anytime?", "Yes. One click in your dashboard. No phone calls, no scripts, no guilt."],
              ["What happens to my members if I cancel?", "Your contacts and member data are exportable as CSV at any time. They're yours."],
              ["What's the difference between Pro ($49) and Unlimited ($79)?", "Only two things: contacts and book covers. Pro ($49/mo) gives you 10,000 contacts and 10 book covers per month. Unlimited ($79/mo) gives you UNLIMITED contacts and UNLIMITED book covers. Everything else — AI Writer, funnels, broadcasts, courses, monthly live workshop, flipbooks on every product, 50/50 commission — is identical on both plans."],
              ["Are courses really free on paid plans?", "Yes. Every course on Faitharama is INCLUDED in both the $49 Pro and $79 Unlimited plans — no separate fees, no upsells, no hidden charges. The only things any member ever pays for are Mike's book, supplements, and Christian merchandise. Everything else is covered."],
              ["How does the affiliate split work?", "<strong>Both paid members earn a flat 50% of what Faitharama.com gets</strong> on every sale they refer. Example: a partner pays Faitharama $10, you get $5, Faitharama keeps $5. <strong>No tiers, no hoops, no thresholds. NO OTHER DEAL LIKE IT.</strong> Sell two memberships per month at 50% and you've already covered your subscription twice over. Your own courses or products you list are yours — sell them anywhere, keep 100%. If you list your book on Books.by <em>through Faitharama</em>, we split that 50/50 because we host, distribute, and market it for you."],
              ["Do I need technical skills?", "Nope. Click LAUNCH on any template, name it, and your funnel is live. We did the hard parts."],
              ["Will my Free Guide leads actually receive the email?", "Yes. We send through your verified Faitharama domain — emails land in real inboxes, not spam."],
            ].map(([q,a]) => (
              <details key={q} className="group rounded-xl border border-border/60 bg-white p-5">
                <summary className="cursor-pointer font-bold text-foreground flex justify-between items-center">
                  <span>{q}</span>
                  <span className="text-gold-deep group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <p className="text-foreground mt-3">{a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {!embedded && <SiteFooter />}
    </div>
  );
}
